// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';

// Third party
import { combineLatest, takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  FavoriteTracksService,
  IFavoriteTrackEvent
} from '@amwager/account/data-access';
import { WagerFacade } from '@amwager/wager/data-access';
import {
  MatNotificationService,
  TranslatePipe,
  UnsubscribeObservableService
} from '@amwager/util';

type FavoriteTab = 'unselected' | 'favorite';

/**
 * Favorite tracks picker (select untracked events, manage favorites)
 */
@Component({
  selector: 'feature-mat-favorite-tracks',
  templateUrl: './favorite-tracks.component.html',
  styleUrls: ['./favorite-tracks.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    TranslatePipe
  ]
})
export class FavoriteTracksComponent
  extends UnsubscribeObservableService
  implements OnInit {

  activeTab: FavoriteTab = 'unselected';

  listOfFavorites: string[] = [];
  allAvailableEvents: IFavoriteTrackEvent[] = [];

  favoriteEvents: IFavoriteTrackEvent[] = [];
  untrackedEvents: IFavoriteTrackEvent[] = [];
  private favoriteEventsCopy: IFavoriteTrackEvent[] = [];
  private untrackedEventsCopy: IFavoriteTrackEvent[] = [];

  selectedEvents: string[] = [];

  isProcessing = false;
  private typeTriggered: FavoriteTab = 'unselected';

  constructor(
    private readonly accountFacade: AccountFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly favoriteTracksService: FavoriteTracksService,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.wagerFacade.getAllEventsNameAndId('FavoriteTracksComponent:ngOnInit');
    this.accountFacade.getAccountPreferencesAPI();

    combineLatest([
      this.wagerFacade.getAllEventsNameAndId$(),
      this.accountFacade.isGetAccountPreferencesSuccess$()
    ])
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(([events, preferences]) => {
        if (events?.isSuccess === undefined || preferences?.isSuccess === undefined)
          return;

        this.allAvailableEvents = events?.response?.AllAvailableEvents ?? [];
        this.listOfFavorites =
          preferences?.response?.AccountPreferences?.ListOfFavorites ?? [];

        const { favorite, untracked } = this.favoriteTracksService.partitionEvents(
          this.allAvailableEvents,
          this.listOfFavorites
        );

        this.favoriteEvents = favorite;
        this.untrackedEvents = untracked;
        this.favoriteEventsCopy = favorite;
        this.untrackedEventsCopy = untracked;

        if (this.isProcessing) {
          this.isProcessing = false;
          this.selectTab(this.typeTriggered);
        }

        this.cdRef.markForCheck();
      });
  }

  /**
   * Switch between the unselected/favorite tabs and clear selection
   * @param {FavoriteTab} tab Tab to activate
   * @returns {void}
   */
  selectTab(tab: FavoriteTab): void {
    this.activeTab = tab;
    this.selectedEvents = [];
  }

  /**
   * Filter the visible list by track name
   * @param {Event} event Input event from the search box
   * @param {boolean} isFavorite True when searching the favorites tab
   * @returns {void}
   */
  onSearch(event: Event, isFavorite: boolean): void {
    const term = (event.target as HTMLInputElement).value ?? '';

    if (isFavorite)
      this.favoriteEvents = this.favoriteTracksService.filterByName(this.favoriteEventsCopy, term);
    else
      this.untrackedEvents = this.favoriteTracksService.filterByName(this.untrackedEventsCopy, term);
  }

  /**
   * Toggle whether an event is selected for the next add action
   * @param {string} eventId Event id
   * @returns {void}
   */
  toggleSelect(eventId: string): void {
    const index = this.selectedEvents.indexOf(eventId);
    if (index > -1) this.selectedEvents.splice(index, 1);
    else this.selectedEvents.push(eventId);
  }

  /**
   * True when an event is currently selected
   * @param {string} eventId Event id
   * @returns {boolean} True when selected
   */
  isSelected(eventId: string): boolean {
    return this.selectedEvents.includes(eventId);
  }

  /**
   * Add the selected untracked events to favorites
   * @returns {void}
   */
  addEvent(): void {
    if (!this.selectedEvents.length || this.isProcessing) return;

    this.isProcessing = true;
    this.listOfFavorites = this.favoriteTracksService.addFavorites(
      this.listOfFavorites,
      this.selectedEvents
    );
    this.typeTriggered = 'favorite';
    this.persistFavorites(this.listOfFavorites);
    this.selectedEvents = [];
  }

  /**
   * Clear the current selection without saving
   * @returns {void}
   */
  refreshSelected(): void {
    this.selectedEvents = [];
  }

  /**
   * Prompt before clearing every favorite track
   * @returns {void}
   */
  clearFavoriteEvents(): void {
    if (!this.listOfFavorites.length) return;

    this.matNotificationService
      .showAlert(
        'Info',
        'Are you sure you wish to remove all tracks from your favorite list?',
        '',
        'Yes',
        'Cancel'
      )
      .subscribe((confirmed: boolean) => {
        if (confirmed) this.doClearFavoriteEvents();
      });
  }

  /**
   * Remove a single track from favorites
   * @param {Event} event Click event (stopped from toggling the row)
   * @param {string} eventId Event id to remove
   * @returns {void}
   */
  removeEvent(event: Event, eventId: string): void {
    event.stopPropagation();
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.listOfFavorites = this.favoriteTracksService.removeFavorite(
      this.listOfFavorites,
      eventId
    );
    this.typeTriggered = 'unselected';
    this.persistFavorites(this.listOfFavorites);
  }

  /**
   * Clear all favorites (after confirmation)
   * @returns {void}
   */
  private doClearFavoriteEvents(): void {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.listOfFavorites = [];
    this.typeTriggered = 'unselected';
    this.persistFavorites([]);
  }

  /**
   * Persist the favorites list via the account facade
   * @param {string[]} favorites Favorites to save
   * @returns {void}
   */
  private persistFavorites(favorites: string[]): void {
    this.accountFacade.upsertAccountPrefenceFieldAPI(
      'ListOfFavorites',
      JSON.stringify(favorites)
    );
  }
}
